// src/components/BarkodTarayici.js
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Dimensions,
  StatusBar,
  Animated,
  Platform,
} from 'react-native';
import { CameraView, Camera } from 'expo-camera';
import { Ionicons } from '@expo/vector-icons';

const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');

// Tarama penceresi boyutları — ekrana göre dinamik
const SCAN_W = Math.round(SCREEN_W * 0.72);
const SCAN_H = Math.round(SCREEN_H * 0.18);
const SCAN_X = (SCREEN_W - SCAN_W) / 2;
const SCAN_Y = (SCREEN_H - SCAN_H) / 2;

// Barkodun merkezi tarama kutusunun içinde mi? (koordinat filtresi)
function merkezKutudaMi({ origin, size }) {
  if (!origin || !size) return false;
  const cx = origin.x + size.width  / 2;
  const cy = origin.y + size.height / 2;
  return (
    cx >= SCAN_X &&
    cx <= SCAN_X + SCAN_W &&
    cy >= SCAN_Y &&
    cy <= SCAN_Y + SCAN_H
  );
}

export default function BarkodTarayici({ visible, onClose, onBarkodOkundu }) {
  const [izinVerildi, setIzinVerildi] = useState(null);
  const [tarandiMi, setTarandiMi]     = useState(false);
  const scanLineAnim                   = useRef(new Animated.Value(0)).current;
  const flashAnim                      = useRef(new Animated.Value(0)).current;
  const loopRef                        = useRef(null);

  const isWeb = Platform.OS === 'web';

  // Kamera izni
useEffect(() => {
  if (visible) {
    setTarandiMi(false);

    (async () => {
      // 1. Önce mevcut izin durumunu kontrol et
      const { status } = await Camera.getCameraPermissionsAsync();

      if (status === 'granted') {
        setIzinVerildi(true);
      } else {
        // 2. Sadece gerekirse izin iste
        const { status: newStatus } = await Camera.requestCameraPermissionsAsync();
        setIzinVerildi(newStatus === 'granted');
      }
    })();

  } else {
    loopRef.current?.stop();
  }
}, [visible]);

  // Tarama çizgisi animasyonu
  useEffect(() => {
    if (!visible || izinVerildi !== true) return;
    loopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(scanLineAnim, { toValue: 1, duration: 1800, useNativeDriver: true }),
        Animated.timing(scanLineAnim, { toValue: 0, duration: 1800, useNativeDriver: true }),
      ])
    );
    loopRef.current.start();
    return () => loopRef.current?.stop();
  }, [visible, izinVerildi]);

  const translateY = scanLineAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: [0, SCAN_H - 2],
  });

  const onBarkodOkuma = ({ data, bounds }) => {
    if (tarandiMi) return;

    // ✅ Web'de bounds YOK → filtreyi kapat
    if (!isWeb && !merkezKutudaMi(bounds)) return;

    setTarandiMi(true);

    Animated.sequence([
      Animated.timing(flashAnim, { toValue: 1, duration: 80, useNativeDriver: true }),
      Animated.timing(flashAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
    ]).start(() => {
      onBarkodOkundu(data);
      onClose();
    });
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />

      <View style={s.container}>
        {/* İzin bekleniyor */}
        {izinVerildi === null && (
          <View style={s.center}>
            <Text style={s.beyazText}>Kamera izni bekleniyor...</Text>
          </View>
        )}

        {/* İzin reddedildi */}
        {izinVerildi === false && (
          <View style={s.center}>
            <Ionicons name="camera-off-outline" size={48} color="#fff" />
            <Text style={[s.beyazText, { marginTop: 16, textAlign: 'center' }]}>
              Kamera izni verilmedi.{'\n'}Lütfen uygulama ayarlarından izin verin.
            </Text>
            <TouchableOpacity style={s.kapatBtn} onPress={onClose}>
              <Text style={{ color: '#fff', fontWeight: '700' }}>Kapat</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Kamera aktif */}
        {izinVerildi === true && (
          <>
          <CameraView
            style={StyleSheet.absoluteFill}
            facing="back"
            {...(isWeb && {
              constraints: {
                video: {
                  facingMode: { exact: "environment" }, // 🔥 en önemli
                  width: { ideal: 1920 },
                  height: { ideal: 1080 },
                },
              },
            })}
            barcodeScannerSettings={{
              barcodeTypes: [
                'ean13', 'ean8', 'upc_a', 'upc_e',
                'code39', 'code128', 'codabar', 'itf14',
                'qr', 'pdf417', 'aztec', 'datamatrix',
              ],
            }}
            onBarcodeScanned={tarandiMi ? undefined : onBarkodOkuma}
          />

            {/* Başarılı okuma flash */}
            <Animated.View
              pointerEvents="none"
              style={[StyleSheet.absoluteFill, { backgroundColor: '#fff', opacity: flashAnim }]}
            />

            {/* ── Karartma katmanları (4 parça, ortası açık) ── */}
            {/* Üst */}
            <View style={[s.overlay, { top: 0, left: 0, right: 0, height: SCAN_Y }]} />
            {/* Alt */}
            <View style={[s.overlay, { top: SCAN_Y + SCAN_H, left: 0, right: 0, bottom: 0 }]} />
            {/* Sol */}
            <View style={[s.overlay, { top: SCAN_Y, left: 0, width: SCAN_X, height: SCAN_H }]} />
            {/* Sağ */}
            <View style={[s.overlay, { top: SCAN_Y, right: 0, width: SCAN_X, height: SCAN_H }]} />

            {/* ── Tarama çerçevesi (köşe imleri + çizgi) ── */}
            <View
              pointerEvents="none"
              style={[s.scanBox, { top: SCAN_Y, left: SCAN_X, width: SCAN_W, height: SCAN_H }]}
            >
              <View style={[s.kose, s.koseTL]} />
              <View style={[s.kose, s.koseTR]} />
              <View style={[s.kose, s.koseBL]} />
              <View style={[s.kose, s.koseBR]} />
              <Animated.View style={[s.scanLine, { transform: [{ translateY }] }]} />
            </View>

            {/* ── Üst başlık ── */}
            <View style={s.header}>
              <TouchableOpacity onPress={onClose} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
                <Ionicons name="close" size={28} color="#fff" />
              </TouchableOpacity>
              <Text style={s.headerTitle}>Barkod Tara</Text>
              <View style={{ width: 28 }} />
            </View>

            {/* ── Alt yönlendirme ── */}
            <View style={s.footer}>
              <Ionicons name="barcode-outline" size={20} color="rgba(255,255,255,0.85)" />
              <Text style={s.footerText}>Barkodu çerçeve içine hizalayın</Text>
            </View>
          </>
        )}
      </View>
    </Modal>
  );
}

const KOSE_SIZE  = 24;
const KOSE_W     = 3;
const KOSE_COLOR = '#FF9800';

const s = StyleSheet.create({
  container:   { flex: 1, backgroundColor: '#000' },
  center:      { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  beyazText:   { color: '#fff', fontSize: 16, lineHeight: 24 },
  kapatBtn:    { marginTop: 24, backgroundColor: '#FF9800', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 },
  overlay:     { position: 'absolute', backgroundColor: 'rgba(0,0,0,0.65)' },
  scanBox:     { position: 'absolute' },
  scanLine:    {
    position: 'absolute', left: 0, right: 0, height: 2,
    backgroundColor: KOSE_COLOR,
    shadowColor: KOSE_COLOR, shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.95, shadowRadius: 6, elevation: 6,
  },
  kose:        { position: 'absolute', width: KOSE_SIZE, height: KOSE_SIZE, borderColor: KOSE_COLOR },
  koseTL:      { top: 0,    left: 0,  borderTopWidth: KOSE_W,    borderLeftWidth:  KOSE_W, borderTopLeftRadius:     3 },
  koseTR:      { top: 0,    right: 0, borderTopWidth: KOSE_W,    borderRightWidth: KOSE_W, borderTopRightRadius:    3 },
  koseBL:      { bottom: 0, left: 0,  borderBottomWidth: KOSE_W, borderLeftWidth:  KOSE_W, borderBottomLeftRadius:  3 },
  koseBR:      { bottom: 0, right: 0, borderBottomWidth: KOSE_W, borderRightWidth: KOSE_W, borderBottomRightRadius: 3 },
  header:      { position: 'absolute', top: Platform.OS === 'android' ? 36 : 56, left: 0, right: 0, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '700', letterSpacing: 0.3 },
  footer:      { position: 'absolute', bottom: 60, left: 0, right: 0, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 8 },
  footerText:  { color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: '500' },
});